import { ContentCard, IllustrationName, ContentCardState, Text, Header, LoadingSpinner } from '@psyomics/ui-components';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { GetServiceUserInvitesQuery, ReferralSessionStatus, ReferralStatus } from '../generated/invites';
import { useServiceUserInvites } from '../hooks/use-service-user-invites';

const images: IllustrationName[] = [
  'ManPointing',
  'ManSitting',
  'WomanSittingOnPhone',
  'WomanSitting',
  'WomanStanding',
  'WomanJumping',
] as const;

const TWO = 2;
const randomImages = [...images].sort(() => Math.random() * TWO - 1);

interface StateToCardStateMappingProps {
  isCompleted: boolean;
  referralCancelled: boolean;
  referralSessionExpired: boolean;
}
const stateToCardStateMapping = ({
  isCompleted,
  referralCancelled,
  referralSessionExpired,
}: StateToCardStateMappingProps): ContentCardState => {
  if (referralCancelled || referralSessionExpired || isCompleted) return 'disabled';
  return 'highlighted';
};

interface StateToMessageMappingProps {
  hasAnswers: boolean;
  isCompleted: boolean;
  referralCancelled: boolean;
  referralSessionExpired: boolean;
}
const stateToMessageMapping = ({
  hasAnswers,
  isCompleted,
  referralCancelled,
  referralSessionExpired,
}: StateToMessageMappingProps): string => {
  if (referralSessionExpired) return 'page.home.cardMessage.expired';
  if (referralCancelled) return 'page.home.cardMessage.cancelled';
  if (isCompleted) return 'page.home.cardMessage.completed';
  if (hasAnswers) return 'page.home.cardMessage.inProgress';
  return 'page.home.cardMessage.notStarted';
};

function sortInvites(invitations: NonNullable<GetServiceUserInvitesQuery['getServiceUserInvites']['invites']>) {
  return invitations.sort((a, b) => Number(new Date(b.createdAt)) - Number(new Date(a.createdAt)));
}

const CheckInContent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { fetching, data } = useServiceUserInvites();

  const onClick = (publicId: string, surveyResponseId?: string) => {
    navigate(`../questionnaire/${publicId}`);
  };

  if (fetching) return <LoadingSpinner />;

  const invites = data?.getServiceUserInvites.invites;
  if (!invites?.length) throw new Error('no invites');

  const sortedInvites = sortInvites(invites);

  return (
    <div className="flex flex-col gap-6 mt-4">
      <Header level="h2">
        {`${t('page.home.greeting')}${sortedInvites[0].givenName ? ' ' + sortedInvites[0].givenName : ''},`}
      </Header>
      <Text size="md">{t('page.home.instructions')}</Text>
      {sortedInvites.map((invite, idx) => {
        return (
          <div key={invite.publicId}>
            <ContentCard
              description={`${t(
                stateToMessageMapping({
                  hasAnswers: Boolean(invite.surveyResponse?.answers),
                  isCompleted: Boolean(invite.surveyResponse?.completedAt),
                  referralCancelled: invite.referralSession.referral.status === ReferralStatus.Cancelled,
                  referralSessionExpired: invite.referralSession.status === ReferralSessionStatus.Expired,
                })
              )}, ${t('page.home.cardMessage.sent')} ${new Date(invite.createdAt).toLocaleDateString()}`}
              image={randomImages[idx % randomImages.length]}
              onClick={() => onClick(invite.publicId, invite.surveyResponseId ?? undefined)}
              state={stateToCardStateMapping({
                isCompleted: Boolean(invite.surveyResponse?.completedAt),
                referralCancelled: invite.referralSession.referral.status === ReferralStatus.Cancelled,
                referralSessionExpired: invite.referralSession.status === ReferralSessionStatus.Expired,
              })}
              title={invite.referralSession.referral.service.description}
            ></ContentCard>
          </div>
        );
      })}
    </div>
  );
};

export { CheckInContent, stateToCardStateMapping, stateToMessageMapping };
